import { useEffect, useState } from "react";
import {
  Check,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Navigation,
  Trash2,
} from "lucide-react";
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useDeleteTripWaypoint, useTripWaypointsReorder } from "@/api/trips";
import { getTaskCareSignals } from "@/features/dispatch/selectors";
import { shipmentReference } from "@/lib/shipment-reference";
import { LaneChips } from "./atoms";
import { CareIcons } from "./care";
import { taskRefs } from "./task-labels";
import type { DerivedStop } from "./trip-stops";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function StatusBadge({ stop, color }: { stop: DerivedStop; color: string }) {
  if (stop.status === "done") {
    return (
      <span
        className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-md text-white"
        style={{ background: "#94a3b8" }}
      >
        <Check className="h-3 w-3" strokeWidth={3} />
      </span>
    );
  }
  if (stop.status === "active") {
    return (
      <span
        className="grid h-[22px] w-[22px] shrink-0 animate-pulse place-items-center rounded-md text-white"
        style={{ background: color }}
      >
        <Navigation className="h-[11px] w-[11px]" />
      </span>
    );
  }
  return (
    <span
      className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-md font-mono text-[11px] font-bold tabular-nums text-white"
      style={{ background: color }}
    >
      {stop.seq}
    </span>
  );
}

function StopBody({ stop, color }: { stop: DerivedStop; color: string }) {
  const done = stop.status === "done";
  const active = stop.status === "active";
  const careTask = stop.tasks.find((t) => getTaskCareSignals(t).hasAny);
  return (
    <>
      <StatusBadge stop={stop} color={color} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span
            className={
              "truncate text-[12.5px] font-semibold " +
              (done ? "text-zinc-400 line-through" : "text-zinc-900")
            }
          >
            {stop.addr}
          </span>
          {active && (
            <span className="shrink-0 rounded-full bg-indigo-600 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-white">
              Next
            </span>
          )}
        </div>
        {stop.contact && (
          <div className="truncate text-[11px] text-zinc-500">
            {stop.contact}
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <LaneChips counts={stop.counts} />
        {careTask && <CareIcons task={careTask} size={17} />}
        {stop.window && (
          <span className="w-12 text-right font-mono text-[11px] font-semibold tabular-nums text-amber-600">
            {stop.window}
          </span>
        )}
        {stop.eta && (
          <span className="w-14 text-right font-mono text-[11px] font-semibold tabular-nums text-zinc-500">
            {stop.eta}
          </span>
        )}
      </div>
    </>
  );
}

function StopTaskDetails({ stop }: { stop: DerivedStop }) {
  return (
    <details className="ml-8 text-[11px] text-zinc-600" open={stop.total > 1}>
      <summary className="cursor-pointer py-1">{stop.total} tasks</summary>
      <div className="mb-2 space-y-1 border-l border-zinc-200 pl-2">
        {stop.tasks.map((task) => {
          const unit = (task.addressLine2 ?? task.address?.line2)?.trim();
          return (
            <div key={task.id} className="break-words">
              <span className="font-mono font-semibold text-zinc-800">
                {shipmentReference(taskRefs(task).shipmentNumber)}
              </span>
              {` · TSK-${task.taskNumber}`}
              {unit && ` · Unit ${unit}`}
              {` · ${task.contactName ?? "No contact"}`}
            </div>
          );
        })}
      </div>
    </details>
  );
}

function ModalStopRow({
  stop,
  color,
  onRemove,
}: {
  stop: DerivedStop;
  color: string;
  onRemove?: (stop: DerivedStop) => void;
}) {
  const active = stop.status === "active";
  const canRemove = onRemove && stop.status !== "done";
  return (
    <div>
      <div
        className={
          "group flex items-center gap-2 rounded-lg py-1.5 pl-1 pr-2 " +
          (active ? "bg-indigo-50 ring-1 ring-indigo-200" : "hover:bg-zinc-50")
        }
      >
        <StopBody stop={stop} color={color} />
        {canRemove && (
          <button
            type="button"
            title="Remove stop from trip"
            onClick={() => onRemove(stop)}
            className="shrink-0 rounded p-1 text-zinc-300 opacity-0 transition hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
          >
            <Trash2 className="h-[15px] w-[15px]" />
          </button>
        )}
      </div>
      <StopTaskDetails stop={stop} />
    </div>
  );
}

function SortableStopRow({
  stop,
  color,
}: {
  stop: DerivedStop;
  color: string;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: stop.id });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className="rounded-lg hover:bg-zinc-50"
    >
      <div className="flex items-center gap-1 py-1.5 pl-0.5 pr-2">
        <button
          type="button"
          className="shrink-0 cursor-grab touch-none px-0.5 text-zinc-300 hover:text-zinc-500"
          {...attributes}
          {...listeners}
          aria-label="Drag to reorder"
        >
          <GripVertical className="h-[15px] w-[15px]" />
        </button>
        <StopBody stop={stop} color={color} />
      </div>
      <StopTaskDetails stop={stop} />
    </div>
  );
}

/** The modal's stop list. Default view collapses completed stops and pins the
 * active one; reorder mode shows a flat, drag-sortable list (dnd-kit) that
 * commits via useTripWaypointsReorder. */
export function ModalStopList({
  stops,
  color,
  tripId,
  reorder,
}: {
  stops: DerivedStop[];
  color: string;
  tripId: string;
  reorder: boolean;
}) {
  const [order, setOrder] = useState<DerivedStop[]>(stops);
  const [showDone, setShowDone] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<DerivedStop | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => setOrder(stops), [stops]);

  const reorderMut = useTripWaypointsReorder(tripId, {
    onSuccess: () => toast.success("Stops reordered"),
    onError: () => toast.error("Couldn't reorder stops"),
  });

  const removeMut = useDeleteTripWaypoint(tripId, {
    onSuccess: () => {
      // Shared hook invalidates by waypoint id; also refresh the trip's
      // waypoint list (keyed by trip id) so this modal's list updates.
      void queryClient.invalidateQueries({ queryKey: ["waypoints", tripId] });
      toast.success("Stop removed from trip");
      setRemoveTarget(null);
    },
    onError: () => toast.error("Couldn't remove stop"),
  });

  const removeDialog = (
    <AlertDialog
      open={!!removeTarget}
      onOpenChange={(o) => !o && setRemoveTarget(null)}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove this stop from the trip?</AlertDialogTitle>
          <AlertDialogDescription>
            {removeTarget?.addr} — its pending tasks return to the unassigned
            pool and can be re-routed. Completed work stays. This can't be
            undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-red-600 hover:bg-red-700"
            onClick={() => removeTarget && removeMut.mutate(removeTarget.id)}
          >
            Remove stop
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  if (stops.length === 0) {
    return (
      <div className="px-3 py-6 text-center text-[12.5px] text-zinc-400">
        No stops on this trip yet
      </div>
    );
  }

  if (reorder) {
    const onDragEnd = (e: DragEndEvent) => {
      const { active, over } = e;
      if (!over || active.id === over.id) return;
      const from = order.findIndex((s) => s.id === active.id);
      const to = order.findIndex((s) => s.id === over.id);
      if (from < 0 || to < 0) return;
      const next = arrayMove(order, from, to);
      setOrder(next);
      reorderMut.mutate(
        next.map((s, i) => ({ waypointId: s.id, sequence: i + 1 }))
      );
    };
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={order.map((s) => s.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="flex flex-col">
            {order.map((s) => (
              <SortableStopRow key={s.id} stop={s} color={color} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    );
  }

  const done = stops.filter((s) => s.status === "done");
  const rest = stops.filter((s) => s.status !== "done");
  const long = stops.length > 16;

  if (!long) {
    return (
      <div className="flex flex-col">
        {stops.map((s) => (
          <ModalStopRow
            key={s.id}
            stop={s}
            color={color}
            onRemove={setRemoveTarget}
          />
        ))}
        {removeDialog}
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {done.length > 0 && (
        <>
          <button
            onClick={() => setShowDone((v) => !v)}
            className="flex items-center gap-2 rounded-lg px-1.5 py-2 text-[11.5px] text-zinc-500 hover:bg-zinc-50 hover:text-zinc-800"
          >
            {showDone ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
            <span
              className="grid h-[18px] w-[18px] place-items-center rounded-full text-white"
              style={{ background: "#cbd5e1" }}
            >
              <Check className="h-3 w-3" strokeWidth={3} />
            </span>
            <span className="font-semibold">{done.length} stops done</span>
          </button>
          {showDone && (
            <div className="mb-1 ml-3 flex flex-col border-l-2 border-zinc-100 pl-2">
              {done.map((s) => (
                <ModalStopRow key={s.id} stop={s} color={color} />
              ))}
            </div>
          )}
        </>
      )}
      {rest.map((s) => (
        <ModalStopRow
          key={s.id}
          stop={s}
          color={color}
          onRemove={setRemoveTarget}
        />
      ))}
      {removeDialog}
    </div>
  );
}
